import { yupResolver } from '@hookform/resolvers/yup';
import React, {
  type ChangeEvent,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
} from 'react';
import { Controller, type UseFormTrigger, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Checkbox } from '@pulse-web-ui/checkbox';

import { StyledLink } from '@src/components';
import {
  USER_PERSONAL_DATA_AGREEMENTS,
  USER_RECL_AGREEMENTS_URL,
} from '@src/constants';
import { agreementsSchema } from '@src/schemas';
import { Store, UserActionTypes } from '@src/store';
import type { UserAgreementsForm } from '@src/store/user';
import type { UserPolice } from '@src/types';

import { StyledCheckboxContainer } from './agreements.styles';

const userPoliciesCode002 = '002';
const userPoliciesCode003 = '003';
const userPoliciesCode005 = '005';
const userPolicies = new Set<string>();

interface Props {
  onAgreementsChange: () => void;
}

export const Agreements = forwardRef<
  {
    trigger: UseFormTrigger<UserAgreementsForm>;
  },
  Props
>(({ onAgreementsChange }, ref) => {
  const {
    state: {
      stateUser: { userAgreementsForm },
    },
    dispatch,
  } = useContext(Store);

  const {
    control,
    formState: { errors },
    trigger,
  } = useForm<UserAgreementsForm>({
    resolver: yupResolver(agreementsSchema),
    shouldFocusError: true,
    mode: 'all',
    defaultValues: {
      userPoliciesCode003: userAgreementsForm?.userPoliciesCode003 || false,
      userPoliciesCode002: userAgreementsForm?.userPoliciesCode002 || false,
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      trigger,
    }),
    [trigger]
  );

  const { t } = useTranslation();

  const handleUserPoliciesCode002Change = useCallback(
    (onChange: (event: ChangeEvent<HTMLInputElement>) => void) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        onAgreementsChange();
        onChange(event);

        if (event.target.checked) {
          userPolicies.add(userPoliciesCode002);
        } else {
          userPolicies.delete(userPoliciesCode002);
        }

        dispatch({
          type: UserActionTypes.SetUserAgreementsForm,
          payload: {
            ...userAgreementsForm,
            userPoliciesCode002: event.target.checked,
          },
        });
      },
    [userAgreementsForm, onAgreementsChange]
  );

  const handleUserPoliciesCode003Change = useCallback(
    (onChange: (event: ChangeEvent<HTMLInputElement>) => void) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        onAgreementsChange();
        onChange(event);

        if (event.target.checked) {
          userPolicies.add(userPoliciesCode003);
          userPolicies.add(userPoliciesCode005);
        } else {
          userPolicies.delete(userPoliciesCode003);
          userPolicies.delete(userPoliciesCode005);
        }

        dispatch({
          type: UserActionTypes.SetUserAgreementsForm,
          payload: {
            ...userAgreementsForm,
            userPoliciesCode003: event.target.checked,
          },
        });
      },
    [userAgreementsForm, onAgreementsChange]
  );

  useEffect(() => {
    const approvedAt = new Date().toISOString();
    const userPoliciesArr: UserPolice[] = [];

    userPolicies.forEach((code: string) => {
      userPoliciesArr.push({
        approvedAt,
        code,
      });
    });

    if (!!userPoliciesArr.length) {
      dispatch({
        type: UserActionTypes.SetUserPolicies,
        payload: userPoliciesArr,
      });
    }
  }, [
    userPolicies.has(userPoliciesCode002),
    userPolicies.has(userPoliciesCode003),
  ]);

  return (
    <StyledCheckboxContainer>
      <Controller
        control={control}
        name="userPoliciesCode002"
        render={({ field: { onChange, value }, fieldState }) => (
          <Checkbox
            checked={value}
            label={
              <Trans
                values={{ href: USER_RECL_AGREEMENTS_URL }}
                components={{ a: <StyledLink /> }}
              >
                {t('AUTH:hints.agreeReceiveSpecialOffers')}
              </Trans>
            }
            name="userPoliciesCode002"
            onChange={handleUserPoliciesCode002Change(onChange)}
            message={
              errors.userPoliciesCode002?.message &&
              (t(errors.userPoliciesCode002.message) || '')
            }
            status={fieldState.error && 'error'}
          />
        )}
      />
      <Controller
        control={control}
        name="userPoliciesCode003"
        render={({ field: { onChange, value }, fieldState }) => (
          <Checkbox
            checked={value}
            label={
              <Trans
                values={{ href: USER_PERSONAL_DATA_AGREEMENTS }}
                components={{ a: <a /> }}
              >
                {t('AUTH:hints.agreePersonalData')}
              </Trans>
            }
            name="userPoliciesCode003"
            onChange={handleUserPoliciesCode003Change(onChange)}
            message={
              errors.userPoliciesCode003?.message &&
              (t(errors.userPoliciesCode003.message) || '')
            }
            status={fieldState.error && 'error'}
          />
        )}
      />
    </StyledCheckboxContainer>
  );
});
